import { Pill } from "components/ui/Pill";
import { useHealthlinkVaccineMessages } from "features/immunizations/hooks/useHealthlinkVaccineMessages";
import { formatDateTime, formatHl7DatesInText } from "utils/dateFormatter";
import {
  formatMessageAction,
  formatMessageReceiver,
  formatMessageStatus,
  getMessageStatusColors,
} from "utils/immunizationStatus";

import styles from "./VaccineDetails.module.css";

const MessageRow = ({ message }) => {
  const colors = getMessageStatusColors(message);
  // The receiver's own words, when it said anything. errorMessage is the parsed reason on a
  // refusal; responseDescription is what an acceptance carries. Neither is guaranteed.
  const reason = message.errorMessage || message.responseDescription;

  return (
    <li className={styles.messageRow}>
      <div className={styles.messageHeader}>
        <span className={styles.messageTitle}>
          {formatMessageReceiver(message.messageType)} ·{" "}
          {formatMessageAction(message.actionType)}
        </span>
        <Pill color={colors.color} bg={colors.bg} border={colors.border}>
          {formatMessageStatus(message)}
        </Pill>
      </div>

      <div className={styles.messageMeta}>
        <span className={styles.healthlinkStatusValue}>
          {message.messageId || "No message id"}
        </span>
        <span className={styles.healthlinkStatusLabel}>
          {message.ackDate
            ? `Acknowledged ${formatDateTime(message.ackDate)}`
            : `Sent ${formatDateTime(message.submissionDate || message.created)}`}
        </span>
      </div>

      {!!reason && (
        <p className={styles.messageReason}>{formatHl7DatesInText(reason)}</p>
      )}
    </li>
  );
};

/**
 * Every message this dose produced, newest first — the per-message granularity of KIN-1826.
 *
 * The two blocks above answer "where does this dose stand"; this one answers "what was
 * actually sent, and what came back each time". A dose created, corrected and cancelled has
 * six rows here and one status up there, and only this view shows that the correction was
 * refused while the original was accepted.
 *
 * A failure to load is stated rather than swallowed: an empty list and an unreachable
 * endpoint must not look the same, or a support conversation starts from "there are no
 * messages" when there are.
 */
export const MessageHistory = ({ patientId, immunisationId }) => {
  const {
    data: messages,
    isLoading,
    isError,
  } = useHealthlinkVaccineMessages(patientId, immunisationId);

  if (isLoading) {
    return <p className={styles.messagesNotice}>Loading messages…</p>;
  }

  if (isError) {
    return (
      <p className={styles.messagesNotice}>
        The message history could not be loaded.
      </p>
    );
  }

  if (!messages?.length) {
    return (
      <p className={styles.messagesNotice}>No messages have been sent yet.</p>
    );
  }

  return (
    <ul className={styles.messageList}>
      {messages.map((message) => (
        <MessageRow key={message.id} message={message} />
      ))}
    </ul>
  );
};
